import React from 'react';
import { NumberField } from '@base-ui/react/number-field';
import { styled } from '@mui/material/styles';

export interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

const Group = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
    }`,
  borderRadius: theme.shape.borderRadius,
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    outline: `1px solid ${theme.palette.primary.main}`,
  },
}));

const Input = styled('input')(({ theme }) => ({
  width: 72,
  textAlign: 'center',
  border: 'none',
  outline: 'none',
  padding: '14px 0',
  fontSize: theme.typography.body1.fontSize,
  fontFamily: theme.typography.fontFamily,
  color: theme.palette.text.primary,
  backgroundColor: 'transparent',
  appearance: 'textfield' as const,
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    display: 'none',
  },
}));

const StepButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 46,
  border: 'none',
  cursor: 'pointer',
  color: theme.palette.text.secondary,
  backgroundColor: 'transparent',
  padding: 0,
  fontSize: '1.25rem',
  lineHeight: 1,
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:active': {
    backgroundColor: theme.palette.action.selected,
  },
  '&[disabled]': {
    cursor: 'default',
    opacity: 0.38,
    pointerEvents: 'none',
  },
}));

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  step = 1,
}) => {
  const handleValueChange = (newValue: number | null) => {
    onChange(newValue ?? min ?? value);
  };

  return (
    <NumberField.Root
      value={value}
      onValueChange={handleValueChange}
      min={min}
      max={max}
      step={step}
    >
      <NumberField.Group render={<Group />}>
        <NumberField.Decrement render={<StepButton type="button" />}>−</NumberField.Decrement>
        <NumberField.Input render={<Input />} />
        <NumberField.Increment render={<StepButton type="button" />}>+</NumberField.Increment>
      </NumberField.Group>
    </NumberField.Root>
  );
};

export default NumberInput;
